import { useState, useEffect } from 'react';
import {
  Loader2, CheckCircle2, RefreshCw,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { diagnosticsAPI } from '../../lib/api';

function getErrorMessage(err) {
  return err?.response?.data?.detail || err?.message || 'Request failed';
}

// ── Coverage gap view ────────────────────────────────────────────────

function CoverageView() {
  const [coverage, setCoverage] = useState(null);
  const [loading, setLoading] = useState(false);
  const [reconciling, setReconciling] = useState(false);

  const loadCoverage = async () => {
    setLoading(true);
    try {
      const { data } = await diagnosticsAPI.commissionVerificationCoverage();
      setCoverage(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadCoverage(); }, []);

  const runReconcile = async () => {
    setReconciling(true);
    try {
      const { data } = await diagnosticsAPI.commissionVerificationReconcile();
      toast.success(`Reconciled: ${data.recomputed} verified, ${data.errors?.length || 0} errors`);
      await loadCoverage();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setReconciling(false);
    }
  };

  const entities = coverage?.by_entity ? Object.entries(coverage.by_entity) : [];

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <Button onClick={loadCoverage} disabled={loading} size="sm" variant="outline">
          {loading ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <RefreshCw className="h-4 w-4 mr-1" />}
          Refresh
        </Button>
        {coverage && coverage.total_gaps > 0 && (
          <Button onClick={runReconcile} disabled={reconciling} size="sm">
            {reconciling && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
            Verify all ({coverage.total_gaps} gaps)
          </Button>
        )}
      </div>

      {loading && !coverage && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground py-4">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading coverage...
        </div>
      )}

      {coverage && coverage.total_gaps === 0 && (
        <div className="flex items-center gap-2 text-sm text-green-600 py-2">
          <CheckCircle2 className="h-4 w-4" />
          All statement (entity, month) pairs have current management fee verification results.
        </div>
      )}

      {coverage && coverage.total_gaps > 0 && (
        <div className="space-y-3">
          <div className="text-sm text-muted-foreground">
            {coverage.total_gaps} gap{coverage.total_gaps !== 1 ? 's' : ''} across {entities.length} entit{entities.length !== 1 ? 'ies' : 'y'}
          </div>

          {entities.sort((a, b) => a[0].localeCompare(b[0])).map(([eid, info]) => (
            <div key={eid} className="border rounded-md p-3">
              <div className="text-sm font-medium mb-2">
                {info.entity_name} <span className="text-muted-foreground font-normal">({eid})</span>
                <span className="ml-2 text-xs text-muted-foreground">{info.count} gap{info.count !== 1 ? 's' : ''}</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {info.months.map((m) => (
                  <span
                    key={m.month}
                    className={`text-xs px-2 py-0.5 rounded font-mono ${
                      m.reason === 'missing'
                        ? 'bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-400'
                        : 'bg-yellow-100 text-yellow-700 dark:bg-yellow-950/40 dark:text-yellow-400'
                    }`}
                    title={m.reason === 'missing' ? 'No verification result' : 'Verification older than latest statement'}
                  >
                    {m.month} ({m.reason})
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Main panel ───────────────────────────────────────────────────────

export default function CommissionVerificationPanel() {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Management Fee Verification Coverage</CardTitle>
      </CardHeader>
      <CardContent>
        <CoverageView />
      </CardContent>
    </Card>
  );
}
